import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { CaseRecordOut, ContactOut, CustomerOut } from "@/api";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { STALE_WORD, VolumeFigure } from "@/components/VolumeFigure";
import { CustomerCard } from "@/features/customer/CustomerCard";
import { HandoverBox } from "@/features/handover/HandoverBox";
import { formatMoney } from "@/lib/format";
import { useNarrow } from "@/lib/useNarrow";

import { CustomerField } from "./CustomerField";
import { markEnds } from "./Feed";
import { takenOver } from "./protocol";
import styles from "./Record.module.css";

const GRATING: Record<string, { name: string; width: string }> = {
  pressrost: { name: "Pressrost", width: "300 mm" },
  barfuss: { name: "Barfuß", width: "288 mm" },
  laengsstab: { name: "Längsstab", width: "288 mm" },
};

/** The grating as the fact sheet names it — type and width (CLAUDE.md Fachregeln). */
export function gratingLabel(type: string | null): string {
  if (type === null) return "nicht gesetzt";
  const known = GRATING[type];
  return known !== undefined ? `${known.name} ${known.width}` : type;
}

/**
 * The same, drawn: the width is a measure and stands in the figure face
 * (UX gate round 1, #13; CI §4) — as the planning board's head writes
 * „Pressrost 300 mm".
 */
function GratingFact({ type }: { type: string | null }) {
  const known = type !== null ? GRATING[type] : undefined;
  if (known === undefined) return <>{gratingLabel(type)}</>;
  return (
    <>
      {known.name} <span className="num">{known.width}</span>
    </>
  );
}

/**
 * The folded fact sheet in one line (UX ruling 5, F-12): „Familie Neumann ·
 * 2.967,10 € · Übergabe offen" — the amount in mono, never „0,00 €".
 */
export function summaryParts(record: CaseRecordOut): { text: string; num: boolean }[] {
  const customer = record.customer;
  const volume = record.case.volume;
  const parts = [
    { text: customer !== null ? (customer.company ?? customer.displayName) : "kein Kunde", num: false },
    volume === null
      ? { text: "noch nicht kalkuliert", num: false }
      : volume.vkTotal === null
        ? { text: "ohne Summe", num: false }
        : { text: formatMoney(volume.vkTotal), num: true },
  ];
  // An amount of an older Stand is no price (UX-Gutachten 23.10.2026,
  // Aufgabe 1): said right after it, in the words the figure uses.
  if (volume !== null && volume.stale) parts.push({ text: STALE_WORD, num: false });
  if (record.handover !== null) parts.push({ text: "Übergabe offen", num: false });
  return parts;
}

/**
 * The register filtered to one customer (UX gate round 1, #8): the id to
 * filter by, and nothing else — a name in an address lands in the history,
 * in logs and in every link passed on (final review of Plan D). The register
 * names the customer from its own answer.
 */
export function customerRegisterUrl(customer: CustomerOut): string {
  const query = new URLSearchParams({ kunde: customer.id });
  return `/vorgaenge?${query.toString()}`;
}

/**
 * The customer as the sheet shows them: the card (`CustomerCard` — name,
 * address, ways to reach them, the contact person where she is somebody else)
 * and, under it, „Weitere Vorgänge", which is the record's and not the
 * customer's: it counts only my own others (D-1, D-3), and leads to them —
 * the register, filtered to this customer (UX gate round 1, #8), where this
 * Vorgang stands among them.
 */
function CustomerFacts({ customer, contact }: { customer: CustomerOut; contact: ContactOut | null }) {
  const others = Math.max(0, customer.myCaseCount - 1);
  const name = customer.company ?? customer.displayName;
  return (
    <div className={styles.customerStack}>
      <CustomerCard customer={customer} contact={contact} />
      <dl className={styles.facts}>
        <div>
          <dt className="label">Weitere Vorgänge</dt>
          <dd>
            {others === 0 ? (
              "keine"
            ) : (
              <>
                <span className="tabular">{others}</span> ·{" "}
                <Link
                  className="inline-link"
                  to={customerRegisterUrl(customer)}
                  aria-label={`Alle Vorgänge von ${name} im Register zeigen`}
                >
                  im Register zeigen
                </Link>
              </>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * `/vorgang/:id?kunde=1` opens „Kunde zuordnen/ändern" on arrival (UX gate
 * round 4, #13) — the register row's „kein Kunde" leads here, as its
 * „Übergeben …" leads to `?uebergeben=1`.
 */
export const CUSTOMER_PARAM = "kunde";

/**
 * The body the sheet shows. „Kunde zuordnen"/„Kunde ändern" open the
 * customer field in a dialog (UX ruling 9) — the list, a nine-field form
 * and the warning about a known address need more room than 17.5 rem, and folding them
 * out here would make the sticky sheet taller than the viewport. The dialog
 * gives the focus back to the button that opened it. Task 11 appends the
 * handover box.
 */
function SheetBody({ record, contact }: { record: CaseRecordOut; contact: ContactOut | null }) {
  const [editing, setEditing] = useState(false);
  // While a customer is created or assigned the dialog holds (record fix
  // round 2, I-2): Escape closed it mid-request and the 409 was lost.
  const [customerBusy, setCustomerBusy] = useState(false);
  const assigned = record.customer !== null;
  // A cancelled Vorgang takes no customer (the server answers 409,
  // spec 2026-10-15 §5): the sheet shows who it is, offers no change.
  const locked = record.case.status === "cancelled";
  const customerRef = useRef<HTMLButtonElement>(null);
  const taken = takenOver(record.timeline);
  const [searchParams, setSearchParams] = useSearchParams();
  function closeCustomer() {
    if (!customerBusy) setEditing(false);
  }

  // `?kunde=1` (UX gate round 4, #13): the customer dialog opens on arrival,
  // from „Kunde zuordnen/ändern", which takes the focus first so the dialog
  // hands it back there — on a phone the folded sheet opens for it. The
  // parameter leaves the address, so a reload or „Zurück" does not open it
  // again. As `?uebergeben=1` in the head.
  const wantsCustomer = useRef(searchParams.get(CUSTOMER_PARAM) === "1");
  useEffect(() => {
    if (!wantsCustomer.current) return undefined;
    // From a timer, so no state is set synchronously in the effect — and
    // after the head has put the focus on the page's heading.
    const timer = window.setTimeout(() => {
      wantsCustomer.current = false;
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(CUSTOMER_PARAM);
          return next;
        },
        { replace: true },
      );
      // …and nothing to open on a Vorgang that takes no customer: the
      // parameter only leaves (a register link from before it was cancelled).
      if (locked) return;
      const button = customerRef.current;
      const fold = button?.closest("details") ?? null;
      if (fold !== null && !fold.open) fold.open = true;
      button?.focus();
      setEditing(true);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [locked, setSearchParams]);
  return (
    <>
      <section className={styles.block} aria-labelledby="sheet-customer">
        <h2 id="sheet-customer" className="label">
          Kunde
        </h2>
        {record.customer !== null ? (
          <CustomerFacts customer={record.customer} contact={contact} />
        ) : (
          <p className={styles.none}>Noch kein Kunde zugeordnet.</p>
        )}
        {locked ? null : (
          <Button ref={customerRef} variant="quiet" flush onClick={() => setEditing(true)}>
            {assigned ? "Kunde ändern" : "Kunde zuordnen"}
          </Button>
        )}
      </section>
      {editing ? (
        <Dialog
          title={assigned ? "Kunde ändern" : "Kunde zuordnen"}
          // The case number in the figure face, as everywhere else (#13).
          subtitle={
            <>
              {record.case.title} · <span className="num">{record.case.caseNo}</span>
            </>
          }
          onClose={closeCustomer}
          busy={customerBusy}
        >
          <CustomerField
            caseId={record.case.id}
            onBusy={setCustomerBusy}
            onDone={() => {
              setCustomerBusy(false);
              setEditing(false);
            }}
            onCancel={closeCustomer}
          />
        </Dialog>
      ) : null}
      <hr className={styles.rule} />
      <dl className={styles.facts}>
        <div>
          <dt className="label">Projektvolumen · VK netto</dt>
          <dd>
            <VolumeFigure volume={record.case.volume} size="sheet" status={record.case.status} />
          </dd>
        </div>
        <div>
          <dt className="label">Zuständig</dt>
          <dd>{record.ownerDisplayName}</dd>
        </div>
        <div>
          <dt className="label">Rosttyp</dt>
          <dd>
            <GratingFact type={record.case.gratingType} />
          </dd>
        </div>
      </dl>
      {/* The state only — the way back is the head's waiting line (UX gate
          round 3, #8). Only where there is one, or where the Vorgang came
          from somebody (UX-Gutachten 2, B-8). */}
      {record.handover !== null || taken !== null ? (
        <>
          <hr className={styles.rule} />
          <HandoverBox handover={record.handover} taken={taken} />
        </>
      ) : null}
    </>
  );
}

/** Wide: the sheet itself. Narrow (≤ 600 px): folded behind its one-line summary. */
function Folding({ record, children }: { record: CaseRecordOut; children: ReactNode }) {
  const narrow = useNarrow();
  if (!narrow) return <>{children}</>;
  const parts = summaryParts(record);
  return (
    <details className={styles.sheetDetails}>
      <summary className={styles.sheetSummary}>
        <span className="label">Stammblatt</span>
        <span className={styles.summaryText}>
          {parts.map((part, index) => (
            <Fragment key={part.text}>
              {index > 0 ? " · " : ""}
              {/* The last part carries the fold chevron and does not wrap,
                  so the chevron never stands alone on a line (R2-11). */}
              <span
                className={
                  [part.num ? "num" : "", index === parts.length - 1 ? styles.summaryLast : ""]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
              >
                {part.text}
              </span>
            </Fragment>
          ))}
        </span>
      </summary>
      <div className={styles.sheetBody}>{children}</div>
    </details>
  );
}

/**
 * The sheet's own scroller (UX-Gutachten 2, B-8): where the card is shorter
 * than what it holds — under a banner, at 700 px — it scrolls in itself, and
 * the end that hides content fades, as the timeline's does (`Feed`,
 * `markEnds`). The mask is on a box inside the card, not on the card: there
 * it would fade the card's own ground and cut its shadow. Marked after each
 * render, on a scroll, and when its box or what it holds changes size.
 */
function SheetScroll({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (ref.current !== null) markEnds(ref.current);
  });
  useEffect(() => {
    const box = ref.current;
    if (box === null || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => markEnds(box));
    observer.observe(box);
    for (const child of box.children) observer.observe(child);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={styles.sheetScroll} onScroll={(event) => markEnds(event.currentTarget)}>
      {children}
    </div>
  );
}

/**
 * The left column of the record (D-7): who, what, how much, with whom — and
 * it stays in view (`sticky`) while the timeline on the right is read, never
 * taller than the viewport (it scrolls in itself, UX ruling 5; its hidden
 * end fades, B-8). The stage is not among its facts: the path over the
 * record says it (UX-Gutachten 2, B-8).
 *
 * `contact` is who wrote the enquiry (D-8): `CaseRecordOut.contact`, which the
 * record screen passes on. The card prints it where she is somebody else than
 * the customer and says nothing when it is absent.
 */
export function FactSheet({ record, contact = null }: { record: CaseRecordOut; contact?: ContactOut | null }) {
  return (
    <aside className={`${styles.panel} ${styles.sheet}`} aria-label="Stammblatt">
      <SheetScroll>
        <Folding record={record}>
          <SheetBody record={record} contact={contact} />
        </Folding>
      </SheetScroll>
    </aside>
  );
}
